/**
 * Swing-speed-aware gear randomizer — generates random EquipmentBag combos
 * from the real equipment library, filtered to the player's speed bracket.
 *
 * A real fitter would never pair a Ventus Black 6 (tour-stiff, 63g) with
 * a 75-mph senior swing, or a Speeder NX 50 (light, whippy) with a 115-mph
 * tour player.  This module enforces those constraints.
 */

import { DRIVERS, IRON_SETS, SHAFTS, GOLF_BALLS } from '../equipment/library';
import type { EquipmentBag, DriverHead, Shaft, IronSet } from '../equipment/types';

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Speed-aware shaft filtering ──────────────────────────────────────────────

/** Filter graphite driver shafts to those appropriate for the player's speed. */
function filterDriverShafts(allShafts: Shaft[], clubSpeed: number): Shaft[] {
  const graphite = allShafts.filter(s => s.material === 'graphite');

  if (clubSpeed < 85) {
    // Slow: light shafts (≤60g), no tour-stiff profiles
    return graphite.filter(s =>
      s.weight <= 60 && s.profile.tipStiffness <= 1.05
    );
  }
  if (clubSpeed < 95) {
    // Moderate: 50-70g, moderate stiffness
    return graphite.filter(s =>
      s.weight >= 50 && s.weight <= 70
    );
  }
  if (clubSpeed < 105) {
    // Fast: 55-75g, prefer mid-to-stiff tip
    return graphite.filter(s =>
      s.weight >= 55 && s.weight <= 75
    );
  }
  // Tour (105+): 60-80g, stiff tips welcome
  return graphite.filter(s =>
    s.weight >= 58 && s.profile.tipStiffness >= 0.95
  );
}

/** Filter steel iron shafts to those appropriate for the player's speed. */
function filterIronShafts(allShafts: Shaft[], clubSpeed: number): Shaft[] {
  const steel = allShafts.filter(s => s.material === 'steel');

  if (clubSpeed < 85) {
    // Slow: lighter steel only (≤115g)
    return steel.filter(s => s.weight <= 115);
  }
  if (clubSpeed < 95) {
    // Moderate: up to 120g
    return steel.filter(s => s.weight <= 125);
  }
  // Fast / Tour: all steel shafts appropriate
  return steel;
}

// ── Speed-aware driver filtering ─────────────────────────────────────────────

/** Filter drivers to those appropriate for the player's speed bracket. */
function filterDrivers(allDrivers: DriverHead[], clubSpeed: number): DriverHead[] {
  if (clubSpeed < 85) {
    // Slow: max-forgiveness, draw-bias heads; no LS (low-spin) models
    return allDrivers.filter(d =>
      !d.id.includes('ls') && !d.id.includes('lst')
    );
  }
  if (clubSpeed < 95) {
    // Moderate: most drivers OK, but skip pure LS tour models
    return allDrivers.filter(d =>
      !d.id.includes('lst')   // G440 LST is too tour-y
    );
  }
  // Fast / Tour: all drivers appropriate
  return allDrivers;
}

// ── Speed-aware iron filtering ───────────────────────────────────────────────

function filterIrons(allIrons: IronSet[], clubSpeed: number): IronSet[] {
  if (clubSpeed < 85) {
    // Slow: game-improvement and players-distance only
    return allIrons.filter(i =>
      i.category === 'game-improvement' || i.category === 'players-distance'
    );
  }
  if (clubSpeed < 95) {
    // Moderate: no blades
    return allIrons.filter(i => i.category !== 'blade');
  }
  if (clubSpeed < 105) {
    // Fast: no game-improvement
    return allIrons.filter(i => i.category !== 'game-improvement');
  }
  // Tour: all irons
  return allIrons;
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Generate N unique random equipment bags filtered to the player's speed.
 *  Ball is fixed (Pro V1 default) — ball selection happens in its own step. */
export function generateRandomGearBags(count: number, clubKey?: string, clubSpeed?: number): EquipmentBag[] {
  const bags: EquipmentBag[] = [];
  const used = new Set<string>();

  // Filter out "Average" placeholders
  const allDrivers = DRIVERS.filter(d => d.id !== 'avg');
  const allIrons = IRON_SETS.filter(s => s.id !== 'avg');
  const allShafts = SHAFTS.filter(s => s.id !== 'avg');

  const speed = clubSpeed ?? 95;
  const isIronClub = clubKey && /iron|wedge|[0-9]i|[0-9]w$/i.test(clubKey);

  // Apply speed-aware filters
  const drivers = filterDrivers(allDrivers, speed);
  const irons = isIronClub ? filterIrons(allIrons, speed) : allIrons;
  const shafts = isIronClub
    ? filterIronShafts(allShafts, speed)
    : filterDriverShafts(allShafts, speed);

  // Fallback: if filtering is too aggressive, use full list
  const safeDrivers = drivers.length >= 3 ? drivers : allDrivers;
  const safeIrons = irons.length >= 2 ? irons : allIrons.filter(i => i.category !== 'blade');
  const safeShafts = shafts.length >= 3 ? shafts : allShafts;

  // Use a consistent ball across all gear combos
  const defaultBall = GOLF_BALLS.find(b => b.id === 'prov1') || GOLF_BALLS.filter(b => b.id !== 'avg')[0];

  let attempts = 0;
  while (bags.length < count && attempts < 200) {
    attempts++;
    const driver = pick(safeDrivers);
    const ironSet = pick(safeIrons);
    const shaft = pick(safeShafts);

    const key = isIronClub
      ? `${ironSet.id}-${shaft.id}`
      : `${driver.id}-${shaft.id}`;
    if (used.has(key)) continue;
    used.add(key);

    bags.push({ driver, irons: ironSet, shaft, ball: defaultBall });
  }
  return bags;
}

/** Build a short label for a gear bag (e.g., "TaylorMade Qi35 Max / Fujikura Ventus Black 6"). */
export function buildGearLabel(bag: EquipmentBag, clubKey: string): string {
  const isDriver = clubKey === 'driver' || clubKey === '3wood';
  const headBrand = isDriver ? bag.driver.brand : bag.irons.brand;
  const headName = isDriver ? bag.driver.name : bag.irons.name;
  const head = headBrand && headBrand !== '--' ? `${headBrand} ${headName}` : headName;
  const shaftBrand = bag.shaft.brand && bag.shaft.brand !== '--' ? `${bag.shaft.brand} ` : '';
  return `${head} / ${shaftBrand}${bag.shaft.name}`;
}
